import { Icon, Tooltip } from "@minervaproject/fds";
import clsx from "clsx";
import { forwardRef, memo } from "react";
import { useDrop } from "react-dnd";

import { EmptyPane } from "./empty_pane";
import { PollSessionPane } from "./poll_session_pane";
import { ResourcePane } from "./resource_pane";
import { UserPane } from "./user_pane";

import { CenteredErrorBoundary } from "components/error_boundary";
import { useCapability } from "hooks/capabilities";
import { useAppDispatch, useAppSelector } from "hooks/redux_hooks";
import { useIsMobile } from "hooks/use_is_mobile";
import { setStagePane } from "shared/reducers/classroom";
import {
  CapabilityType,
  DraggableItemType,
  FeaturedPollResponseType,
  IconPropsType,
  PaneContentType,
} from "shared/types";
import { classroomSelectors, localSelectors } from "store";

// TODO: Put into FDS
const VisibleCrossIcon = forwardRef<HTMLElement, IconPropsType>((props, ref) => {
  const width = "16";
  const height = "16";
  const viewBox = `0 0 ${width} ${height}`;
  return (
    <Icon width={width} height={height} viewBox={viewBox} {...props} ref={ref}>
      <svg fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M14.6004 5.59961L6.40039 13.7996C6.90039 13.8996 7.50039 13.9996 8.00039 13.9996C11.6004 13.9996 14.4004 10.8996 15.6004 9.09961C16.1004 8.39961 16.1004 7.49961 15.6004 6.79961C15.4004 6.49961 15.0004 6.09961 14.6004 5.59961Z"
          fill={props?.color || "#676767"}
        />
        <path
          d="M14.3 0.3L11.6 3C10.5 2.4 9.3 2 8 2C4.4 2 1.6 5.1 0.4 6.9C-0.1 7.6 -0.1 8.5 0.4 9.1C0.9 9.9 1.8 10.9 2.8 11.8L0.3 14.3C-0.1 14.7 -0.1 15.3 0.3 15.7C0.5 15.9 0.7 16 1 16C1.3 16 1.5 15.9 1.7 15.7L15.7 1.7C16.1 1.3 16.1 0.7 15.7 0.3C15.3 -0.1 14.7 -0.1 14.3 0.3ZM5.3 9.3C5.1 8.9 5 8.5 5 8C5 6.3 6.3 5 8 5C8.5 5 8.9 5.1 9.3 5.3L5.3 9.3Z"
          fill={props?.color || "#676767"}
        />
      </svg>
    </Icon>
  );
});

function PaneVisibility({ onClick }: { onClick: () => void }) {
  return (
    <div className="stage-pane-tooltip" onClick={onClick} style={{ zIndex: 9999 }}>
      <Tooltip
        isGlobal
        place="bottom"
        className="body"
        backgroundColor="var(--black-tint-20)"
        iconComponent={
          <div className="stage-pane-tooltip-circle">
            <VisibleCrossIcon color="white" width={16} height={16} />
          </div>
        }
      >
        <div className="body-s" style={{ width: "160px" }}>
          This pane is hidden from Presentation View. Click to include it.
        </div>
      </Tooltip>
    </div>
  );
}

const Pane = memo(function PaneComponent({
  contentId,
  contentType,
  featuredPollResponse,
  index,
  isSmallPane,
  showingHiddenNote,
  isPinned,
}: {
  contentId: string | undefined;
  contentType: PaneContentType;
  featuredPollResponse?: FeaturedPollResponseType;
  index: number;
  isSmallPane: boolean;
  showingHiddenNote?: boolean;
  isPinned?: boolean;
}) {
  const dispatch = useAppDispatch();
  const currentUserId = useAppSelector(localSelectors.selectCurrentUserId());
  const isMobile = useIsMobile();
  const breakoutRoom = useAppSelector(classroomSelectors.selectActiveBreakoutRoomForCurrentUser());
  const canControlClassroom = useCapability(CapabilityType.CAN_CONTROL_CLASSROOM);
  const activeBreakoutSessionId = useAppSelector(
    classroomSelectors.selectActiveBreakoutSessionId(),
  );

  const [, drop] = useDrop({
    accept: [
      PaneContentType.PollSession,
      PaneContentType.Resource,
      PaneContentType.User,
      DraggableItemType.DirectoryUser,
    ],
    drop: async (
      item: {
        id: string;
        callback?: () => Promise<string>;
        directoryCallback?: (args: { paneIndex: number }) => void;
      },
      monitor,
    ) => {
      if (monitor.getItemType() === DraggableItemType.DirectoryUser && item.directoryCallback) {
        item.directoryCallback({ paneIndex: index });
      } else {
        const itemType = monitor.getItemType() as PaneContentType;
        let id = item.id;
        if (item.callback) {
          id = await item.callback();
        }
        dispatch(
          setStagePane({
            index,
            contentType: itemType,
            contentId: id,
            breakoutRoomId: breakoutRoom?.id,
            ownerId: currentUserId,
          }),
        );
      }
    },
  });

  const shareInPresentationView = () => {
    dispatch(
      setStagePane({
        index,
        contentType,
        contentId,
      }),
    );
  };

  return (
    <div
      className={clsx(
        `pane-${index} flex highlighted vertical-scroll`,
        isMobile ? "flex-auto" : "relative",
      )}
      ref={drop}
      data-testid="stage-pane"
      style={{
        margin: contentType === PaneContentType.Resource ? "2px" : "0px",
      }}
    >
      {showingHiddenNote && contentType !== PaneContentType.Empty && (
        <PaneVisibility onClick={shareInPresentationView} />
      )}
      {(() => {
        switch (contentType) {
          case PaneContentType.PollSession:
            return (
              contentId && (
                <CenteredErrorBoundary text="This poll cannot be displayed">
                  <PollSessionPane
                    pollSessionId={contentId}
                    featuredPollResponse={featuredPollResponse}
                  />
                </CenteredErrorBoundary>
              )
            );
          case PaneContentType.Resource:
            return (
              contentId && (
                <CenteredErrorBoundary text="This resource cannot be displayed">
                  <ResourcePane resourceId={contentId} />
                </CenteredErrorBoundary>
              )
            );
          case PaneContentType.User:
            return (
              contentId && (
                <CenteredErrorBoundary text="This user cannot be displayed">
                  <UserPane
                    userId={contentId}
                    currentUserCanControlClassroom={canControlClassroom}
                    isSmallPane={isSmallPane}
                    isPinned={!!isPinned}
                    index={index}
                  />
                </CenteredErrorBoundary>
              )
            );
          default:
            return <EmptyPane canModifyStage={canControlClassroom || !!activeBreakoutSessionId} />;
        }
      })()}
    </div>
  );
});

export { Pane, PaneVisibility, VisibleCrossIcon };
